import { memo } from 'react';
import { formatDateSafe } from '../../../../utilities/date';

const money = (v) => Number(v || 0).toLocaleString('es-PE', { style: 'currency', currency: 'PEN' });

function getEstadoStyles(estado) {
  const e = String(estado || '').toLowerCase();
  if (e === 'activo') return 'bg-green-100 text-green-800';
  if (e === 'cancelado') return 'bg-gray-100 text-black';
  if (e === 'liquidado') return 'bg-blue-100 text-blue-800';
  return 'bg-gray-100 text-gray-800';
}

function getModalidadStyles(modalidad) {
  const m = String(modalidad || '').toUpperCase();
  if (m === 'NUEVO') return 'bg-green-100 text-green-800';
  if (m === 'RCS') return 'bg-blue-100 text-blue-800';
  if (m === 'RSS') return 'bg-purple-100 text-purple-800';
  if (m === 'INACTIVO') return 'bg-amber-100 text-amber-800';
  return 'bg-gray-100 text-gray-800';
}

function getAbonadoPorStyles(abonado) {
  const a = String(abonado || '').toUpperCase();
  if (a === 'CUENTA CORRIENTE') return 'bg-red-100 text-red-800 border border-red-200';
  if (a === 'CAJA CHICA') return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
  return 'bg-gray-100 text-gray-800';
}

function getProgressBarColor(percentage) {
  if (percentage >= 75) return 'bg-green-500';
  if (percentage >= 50) return 'bg-yellow-500';
  if (percentage >= 25) return 'bg-orange-500';
  return 'bg-red-500';
}

const LoansTable = ({
  rows = [],
  loading = false,
  error = '',
}) => {
  return (
    <>
      {error && (
        <div className="mt-3 bg-red-50 text-red-700 p-4 rounded-lg border border-red-200">
          {error}
        </div>
      )}

      <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-500 bg-gray-50 border-b border-gray-200">
                <th className="py-3 px-3 text-center">PRÉSTAMO</th>
                <th className="py-3 px-3 text-center">CLIENTE</th>
                <th className="py-3 px-3 text-center">ASESOR</th>
                <th className="py-3 px-3 text-center">FECHA DESEMBOLSO</th>
                <th className="py-3 px-3 text-center">MONTO</th>
                <th className="py-3 px-3 text-center">INTERÉS</th>
                <th className="py-3 px-3 text-center">TOTAL</th>
                <th className="py-3 px-3 text-center">FRECUENCIA</th>
                <th className="py-3 px-3 text-center">CUOTAS</th>
                <th className="py-3 px-3 text-center">CAPITAL PENDIENTE</th>
                <th className="py-3 px-3 text-center">SALDO PENDIENTE</th>
                <th className="py-3 px-3 text-center">MODALIDAD</th>
                <th className="py-3 px-4 text-center" style={{ minWidth: '140px' }}>ABONADO POR</th>
                <th className="py-3 px-4 text-center" style={{ minWidth: '160px' }}>REGISTRADO POR</th>
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td className="py-8 px-4 text-center text-gray-500" colSpan={14}>
                    {loading ? 'Cargando…' : 'Sin resultados'}
                  </td>
                </tr>
              )}

              {rows.map((r) => {
                return (
                  <tr
                    key={r.id}
                    className="border-b border-gray-200 last:border-b-0 transition hover:bg-rose-50/40 odd:bg-white even:bg-gray-50/40"
                  >
                    <td className="py-3 px-3">
                      <div className="flex flex-col items-center text-center gap-1">
                        <span className="font-medium text-gray-900">
                          #{r.id}
                        </span>

                        <span className={`text-xs px-2 py-0.5 rounded-full w-fit ${getEstadoStyles(r.estado)}`}>
                          {(r.estado || '—').toUpperCase()}
                        </span>
                      </div>
                    </td>

                    {/* Cliente */}
                    <td className="py-3 px-3">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-xs">
                          {r.cliente || r.cliente_id || '—'}
                        </span>
                      </div>
                    </td>

                    {/* Asesor */}
                    <td className="py-3 px-3">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-xs">
                          {r.asesor || r.asesor_id || '—'}
                        </span>
                      </div>
                    </td>

                    {/* Fecha Inicio */}
                    <td className="py-3 px-3 text-center">
                      <span className="text-xs font-medium text-gray-900">
                        {formatDateSafe(r.fecha_generacion)}
                      </span>
                    </td>

                    {/* Monto */}
                    <td className="py-3 px-3 text-center">
                      <span className="font-medium text-gray-900">{money(r.monto)}</span>
                    </td>

                    {/* Interés */}
                    <td className="py-3 px-3 text-center">
                      <span className="font-medium text-gray-900">
                        {(r.interes_porcentaje || 0).toFixed(2)}%
                      </span>
                    </td>

                    {/* Total */}
                    <td className="py-3 px-3 text-center">
                      <span className="font-medium text-gray-900">{money(r.total)}</span>
                    </td>

                    {/* Frecuencia */}
                    <td className="py-3 px-3 text-center">
                      <span className="text-xs font-medium text-gray-700 capitalize">
                        {(r.frecuencia || '—').toUpperCase()}
                      </span>
                    </td>

                    {/* Cuotas con barra de progreso */}
                    <td className="py-3 px-3 text-center">
                      <div className="space-y-1.5">
                        <div className="text-sm font-medium text-gray-900">
                          {r.progreso_cuotas || '0 de 0'}
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full transition-all duration-300 ${getProgressBarColor(r.porcentaje_completado || 0)}`}
                            style={{ width: `${Math.min(100, Math.max(0, r.porcentaje_completado || 0))}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-gray-600">
                          {(r.porcentaje_completado || 0).toFixed(1)}%
                        </div>
                      </div>
                    </td>

                    {/* Capital pendiente */}
                    <td className="py-3 px-3 text-center">
                      <span className="font-semibold text-gray-900">
                        {money(r.capital_pendiente)}
                      </span>
                    </td>

                    {/* Saldo Pendiente + Renta Pendiente */}
                    <td className="py-3 px-3 text-center">
                      <div className="flex flex-col gap-1 items-center">
                        <span className="font-semibold text-gray-900">
                          {money(r.saldo_pendiente)}
                        </span>
                        <span className="text-xs text-gray-500">
                          RENTA: {money(r.renta_pendiente)}
                        </span>
                      </div>
                    </td>

                    {/* Modalidad */}
                    <td className="py-3 px-3 text-center">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${getModalidadStyles(r.modalidad)}`}>
                        {r.modalidad || '—'}
                      </span>
                    </td>

                    {/* Abonado Por */}
                    <td className="py-3 px-4 text-center" style={{ minWidth: '140px' }}>
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap ${getAbonadoPorStyles(r.abonado_por)}`}>
                        {r.abonado_por || 'N/A'}
                      </span>
                    </td>
                    
                    {/* Registrado Por */}
                    <td className="py-3 px-4 text-left" style={{ minWidth: '160px' }}>
                      <span className="text-xs font-medium text-gray-900">
                        {r.registrado_por || '-'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        {loading && rows.length > 0 && (
          <div className="p-4">
            <div className="mx-auto h-1 w-32 bg-gray-100 rounded overflow-hidden">
              <div className="h-full w-1/2 animate-pulse bg-red-600"></div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default memo(LoansTable);
